import { usePortfolio } from '../../context/PortfolioContext';
import { useRef, useMemo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

const COLORS = [
  '#00d4ff', // cyan
  '#22c55e', // green  
  '#f97316', // orange
  '#8b5cf6', // purple
  '#f59e0b', // amber
  '#ec4899', // pink
  '#06b6d4', // teal
  '#84cc16', // lime
  '#ef4444', // red
  '#6366f1', // indigo
];

// Get sector from backend API data only
// Backend API: metadataApi.getSectors(tickers) is called in PortfolioContext
const getSectorFromTicker = (ticker, sectors) => {
  if (!ticker || !sectors) return 'Unknown';
  
  // Try exact match with uppercase ticker
  const upperTicker = ticker.toUpperCase();
  if (sectors[upperTicker]) {
    return sectors[upperTicker];
  }
  
  // Try with base ticker (without exchange suffix)
  const baseTicker = upperTicker.split('.')[0];
  if (sectors[baseTicker]) {
    return sectors[baseTicker];
  }
  
  // Try with original case
  if (sectors[ticker]) {
    return sectors[ticker];
  }
  
  // Return Unknown if not found in backend data
  return 'Unknown';
};

function SectorAllocationChart() {
  const { portfolio, sectors, isLoading } = usePortfolio();
  
  // Keep last valid data to prevent flashing during refreshes
  const lastValidDataRef = useRef(null);
  const hasDataEverLoadedRef = useRef(false);
  
  // Calculate current sector data
  const currentSectorData = useMemo(() => {
    if (!portfolio || !portfolio.holdings || portfolio.holdings.length === 0) {
      return null;
    }
    
    const sectorValues = {};
    let totalValue = 0;
    let hasValidSector = false;

    portfolio.holdings.forEach(holding => {
      const sector = getSectorFromTicker(holding.ticker, sectors);
      const marketValue = (holding.quantity || 0) * (holding.price_gbp || holding.price || 0);
      
      // Track if we have at least one valid sector (not Unknown)
      if (sector !== 'Unknown') {
        hasValidSector = true;
      }
      
      if (!sectorValues[sector]) {
        sectorValues[sector] = 0;
      }
      sectorValues[sector] += marketValue;
      totalValue += marketValue;
    });
    
    const sortedSectors = Object.entries(sectorValues)
      .sort((a, b) => b[1] - a[1])
      .filter(([_, value]) => value > 0.01);
    
    if (sortedSectors.length === 0) {
      return null;
    }
    
    return {
      sortedSectors,
      totalValue,
      hasValidSector
    };
  }, [portfolio, sectors]);
  
  // Update last valid data when we have valid data
  if (currentSectorData && currentSectorData.hasValidSector) {
    lastValidDataRef.current = currentSectorData;
    hasDataEverLoadedRef.current = true;
  }
  
  // Only use cached data if currently loading (prevents flashing during refresh)
  // If data is loaded but empty (sold all positions), show empty state
  const isCurrentlyLoading = isLoading && !currentSectorData;
  const displayData = isCurrentlyLoading ? (lastValidDataRef.current || currentSectorData) : currentSectorData;
  const isInitialLoading = isLoading && !hasDataEverLoadedRef.current;
  const hasNoDataEver = !displayData && !hasDataEverLoadedRef.current;

  if (isInitialLoading || hasNoDataEver) {
    return (
      <div className="chart-container">
        <div className="chart-header">
          <span className="chart-title">Sector Allocation</span>
        </div>
        <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="text-muted">No sector data available</span>
        </div>
      </div>
    );
  }

  // Use display data (current or last valid)
  const { sortedSectors, totalValue } = displayData;

  const chartData = {
    labels: sortedSectors.map(([sector, _]) => sector),
    datasets: [
      {
        data: sortedSectors.map(([_, value]) => value),
        backgroundColor: COLORS.slice(0, sortedSectors.length),
        borderColor: 'rgba(12, 28, 48, 0.8)',
        borderWidth: 2,
        hoverOffset: 8,
      },
    ],
    items: sortedSectors.map(([sector, value], index) => ({
      label: sector,
      value: value,
      percent: (value / totalValue) * 100,
      color: COLORS[index % COLORS.length]
    }))
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        display: false, // Hide default legend
      },
      tooltip: {
        backgroundColor: 'rgba(12, 28, 48, 0.96)',
        borderColor: 'rgba(0, 212, 255, 0.35)',
        borderWidth: 1,
        titleColor: '#dff0ff',
        bodyColor: '#dff0ff',
        padding: 10,
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const percentage = ((value / totalValue) * 100).toFixed(1);
            return `${context.label}: ${formatCurrency(value)} (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <div className="chart-container">
      <div className="chart-header">
        <span className="chart-title">Sector Allocation</span>
        <span className="data-table-badge">By Market Value</span>
      </div>
      
      <div className="donut-layout">
        <div className="donut-canvas">
          <Doughnut data={chartData} options={options} />
        </div>

        <div className="donut-legend">
          {chartData.items.map((item, idx) => (
            <div 
              key={item.label} 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '0.5rem',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span 
                  style={{ 
                    width: '10px', 
                    height: '10px', 
                    borderRadius: '50%', 
                    backgroundColor: item.color,
                    flexShrink: 0,
                  }} 
                />
                <span style={{ fontSize: '0.8125rem', fontWeight: 500 }}>{item.label}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, fontFamily: 'Space Mono' }}>
                  {formatCurrency(item.value)}
                </div>
                <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                  {item.percent.toFixed(1)}%
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SectorAllocationChart;
